import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { campaignAPI, paymentAPI, stripeConnectAPI } from '../services/api';
import { currencies as allCurrencies } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { trackDonation, trackEvent } from '../utils/analytics';
import { getStoredReferralCode } from '../utils/referralTracking';
import {
  ArrowLeft,
  Heart,
  CreditCard,
  Loader2,
  ChevronDown,
  Shield,
  Lock,
  Check,
  Sparkles,
  Users,
  Clock,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

const DonatePage = () => {
  const { campaignId: id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  
  // Team page support
  const teamPageId = searchParams.get('team_page');
  const [teamPage, setTeamPage] = useState(null);
  
  // Referral code from URL or localStorage
  const urlRefCode = searchParams.get('ref');
  const storedRefCode = getStoredReferralCode();
  const referralCode = urlRefCode || storedRefCode;
  
  // Campaign data
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Donation form
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(allCurrencies[0]);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');
  
  // Tip
  const [tipPercentage, setTipPercentage] = useState(15);
  const [customTipAmount, setCustomTipAmount] = useState('');
  const [showCustomTip, setShowCustomTip] = useState(false);
  
  // Donor info
  const [donorEmail, setDonorEmail] = useState(user?.email || '');
  const [donorFirstName, setDonorFirstName] = useState('');
  const [donorLastName, setDonorLastName] = useState('');
  const [donorMessage, setDonorMessage] = useState('');
  
  // Options
  const [hideFromPublic, setHideFromPublic] = useState(false);
  const [optInMarketing, setOptInMarketing] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  
  // Payment
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [campaignError, setCampaignError] = useState(null); // Specific error for campaign fetch
  const [creatorHasStripeConnect, setCreatorHasStripeConnect] = useState(false);

  // Preset amounts
  const presetAmounts = [25, 50, 100, 250, 500, 1000];
  
  // Filter currencies
  const filteredCurrencies = allCurrencies.filter(c =>
    c.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.name.toLowerCase().includes(currencySearch.toLowerCase())
  );

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const data = await campaignAPI.get(id);
        setCampaign(data);
        
        // Check if creator has Stripe Connect
        if (data.creator_stripe_connect_active) {
          setCreatorHasStripeConnect(true);
        }
        
        // Fetch team page info if donating via team page
        if (teamPageId) {
          try {
            const teamRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/team-pages/${teamPageId}`);
            if (teamRes.ok) {
              const teamData = await teamRes.json();
              setTeamPage(teamData);
            }
          } catch (err) {
            console.error('Failed to fetch team page:', err);
          }
        }
      } catch (err) {
        console.error('Failed to fetch campaign:', err);
        // Log more details for debugging
        if (err.response) {
          console.error('Response status:', err.response.status);
          console.error('Response data:', err.response.data);
          
          // Set specific error based on response
          const detail = err.response.data?.detail || '';
          if (err.response.status === 404) {
            if (detail.includes('pending')) {
              setCampaignError({ type: 'pending', message: 'This campaign is pending review and not yet accepting donations.' });
            } else if (detail.includes('draft')) {
              setCampaignError({ type: 'draft', message: 'This campaign is still in draft mode.' });
            } else if (detail.includes('inactive') || detail.includes('paused')) {
              setCampaignError({ type: 'paused', message: 'This campaign has been paused and is not accepting donations.' });
            } else if (detail.includes('deleted')) {
              setCampaignError({ type: 'deleted', message: 'This campaign has been removed.' });
            } else {
              setCampaignError({ type: 'not_found', message: 'Campaign not found or no longer available.' });
            }
          } else {
            setCampaignError({ type: 'error', message: 'Failed to load campaign. Please try again.' });
          }
        } else {
          setCampaignError({ type: 'error', message: 'Network error. Please check your connection.' });
        }
        // Campaign fetch failed - setCampaign will remain null, showing error state
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCampaign();
    } else {
      setLoading(false);
    }
  }, [id, teamPageId]);

  // Calculate amounts
  const getBaseAmount = () => parseFloat(donationAmount || customAmount || 0);
  const getTipAmount = () => {
    if (showCustomTip && customTipAmount) {
      return parseFloat(customTipAmount);
    }
    return (getBaseAmount() * tipPercentage) / 100;
  };
  const getTotalAmount = () => getBaseAmount() + getTipAmount();
  const getProgressPercent = () => campaign ? Math.min((campaign.raised / campaign.goal) * 100, 100) : 0;

  const handleDonate = async () => {
    const amount = getBaseAmount();
    if (!amount || amount <= 0) {
      setError('Please enter a valid donation amount');
      return;
    }
    
    // Check if donation exceeds remaining goal
    if (campaign && campaign.goal > 0) {
      const remaining = campaign.goal - (campaign.raised || 0);
      if (remaining <= 0) {
        setError('This campaign has already reached its fundraising goal');
        return;
      }
      if (amount > remaining) {
        setError(`Donation exceeds the remaining goal. Maximum allowed: ${selectedCurrency.symbol}${remaining.toFixed(2)}`);
        return;
      }
    }
    
    // Recurring donations require minimum $5
    if (isRecurring && amount < 5) {
      setError('Monthly donations require a minimum of $5');
      return;
    }

    // Recurring donations require email
    if (isRecurring && !donorEmail) {
      setError('Email is required for monthly donations');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Handle recurring donations
      if (isRecurring) {
        const subscriptionData = {
          campaign_id: campaign.id,
          amount: amount,
          currency: selectedCurrency.code.toLowerCase(),
          donor_name: hideFromPublic ? 'Anonymous' : `${donorFirstName} ${donorLastName}`.trim() || 'Anonymous',
          donor_email: donorEmail,
          message: donorMessage || '',
          is_anonymous: hideFromPublic,
          origin_url: window.location.origin
        };
        
        // Track event
        trackEvent('begin_subscription', 'Recurring Donation', campaign.title, amount);
        
        // Get auth token from localStorage
        const authToken = localStorage.getItem('token');
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          },
          body: JSON.stringify(subscriptionData)
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Failed to create subscription');
        }
        
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          throw new Error('Failed to get checkout URL');
        }
        return;
      }

      // One-time donation (existing logic)
      const donationData = {
        campaignId: campaign.id,
        amount: amount,
        tipAmount: getTipAmount(),
        totalAmount: getTotalAmount(),
        currency: selectedCurrency.code.toLowerCase(),
        donorName: hideFromPublic ? 'Anonymous' : `${donorFirstName} ${donorLastName}`.trim() || 'Anonymous',
        donorEmail: donorEmail || undefined,
        donorId: user?.id || null,  // Include user ID for dashboard tracking
        message: donorMessage || '',
        isAnonymous: hideFromPublic,
        isGuest: !user,
        optInMarketing: optInMarketing,
        paymentMethod: paymentMethod,
        teamPageId: teamPageId || undefined,  // Include team page ID for team fundraising
        referralCode: referralCode || undefined,  // Include referral code for tracking
      };

      // Handle different payment methods
      switch (paymentMethod) {
        case 'apple_pay':
        case 'google_pay':
          // Apple Pay and Google Pay use Stripe's Payment Request API
          // They will be available in the Stripe checkout if the browser supports them
          const response = await paymentAPI.createDonationSession(donationData);
          if (response.checkout_url) {
            window.location.href = response.checkout_url;
          }
          break;
          
        case 'qr_code':
          // QR Code payment - show QR code modal
          setError('QR Code payment coming soon. Please use Credit Card or Apple/Google Pay for now.');
          setIsSubmitting(false);
          return;
          
        case 'card':
        default:
          // Use Stripe Connect for split payments if creator has it set up
          if (creatorHasStripeConnect && paymentMethod === 'card') {
            try {
              // Track donation attempt
              trackDonation(campaign.id, amount, selectedCurrency.code);
              trackEvent('begin_checkout', 'Donation', campaign.title, amount);
              
              const connectResponse = await stripeConnectAPI.createSplitPayment({
                campaignId: campaign.id,
                donationAmount: amount,
                tipAmount: getTipAmount(),
                currency: selectedCurrency.code.toLowerCase(),
                donorName: hideFromPublic ? 'Anonymous' : `${donorFirstName} ${donorLastName}`.trim() || 'Anonymous',
                donorEmail: donorEmail,
                message: donorMessage || '',
                isAnonymous: hideFromPublic,
                teamPageId: teamPageId || null  // Include team page ID for team fundraising
              });
              if (connectResponse.checkout_url) {
                window.location.href = connectResponse.checkout_url;
              } else {
                throw new Error('Failed to get checkout URL');
              }
            } catch (connectErr) {
              console.error('Stripe Connect payment failed, falling back to standard:', connectErr);
              // Fall back to standard payment
              const stripeResponse = await paymentAPI.createDonationSession(donationData);
              if (stripeResponse.checkout_url) {
                window.location.href = stripeResponse.checkout_url;
              } else {
                throw new Error('Failed to get checkout URL');
              }
            }
          } else {
            // Standard Stripe checkout
            const stripeResponse = await paymentAPI.createDonationSession(donationData);
            if (stripeResponse.checkout_url) {
              window.location.href = stripeResponse.checkout_url;
            } else {
              throw new Error('Failed to get checkout URL');
            }
          }
          break;
      }
    } catch (err) {
      console.error('Donation failed:', err);
      setError(err.response?.data?.detail || 'Failed to process donation. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!campaign) {
    // Determine the error icon and colors based on error type
    const errorType = campaignError?.type || 'not_found';
    const errorMessages = {
      pending: {
        title: 'Campaign Pending Review',
        message: 'This campaign is currently being reviewed by our team and is not yet accepting donations. Please check back later.',
        icon: Clock,
        color: 'amber'
      },
      draft: {
        title: 'Campaign Not Published',
        message: 'This campaign is still in draft mode and has not been published yet.',
        icon: AlertCircle,
        color: 'amber'
      },
      paused: {
        title: 'Campaign Paused',
        message: 'This campaign has been temporarily paused and is not accepting donations at this time.',
        icon: AlertCircle,
        color: 'amber'
      },
      deleted: {
        title: 'Campaign Removed',
        message: 'This campaign has been removed and is no longer available.',
        icon: AlertCircle,
        color: 'red'
      },
      not_found: {
        title: t('campaignNotFoundMsg'),
        message: 'This campaign may not exist or is no longer available.',
        icon: Heart,
        color: 'red'
      },
      error: {
        title: 'Unable to Load Campaign',
        message: campaignError?.message || 'There was an error loading this campaign. Please try again.',
        icon: AlertCircle,
        color: 'red'
      }
    };
    
    const errorInfo = errorMessages[errorType] || errorMessages.not_found;
    const ErrorIcon = errorInfo.icon;
    const colorClasses = errorInfo.color === 'amber' 
      ? 'bg-amber-500/10 text-amber-500' 
      : 'bg-destructive/10 text-destructive';

    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${colorClasses} flex items-center justify-center`}>
            <ErrorIcon className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-semibold mb-2">{errorInfo.title}</h2>
          <p className="text-muted-foreground mb-6">
            {errorInfo.message}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              variant="outline" 
              onClick={() => window.location.reload()}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
            <Button data-testid="browse-campaigns-btn" onClick={() => navigate('/discover')}>
              Browse Active Campaigns
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Check if campaign is accepting donations
  const canAcceptDonations = campaign.status === 'active';
  
  if (!canAcceptDonations) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Heart className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold mb-2">{campaign.title}</h2>
          <p className="text-muted-foreground mb-2">
            {campaign.status === 'draft' && 'This campaign is still being prepared and not yet accepting donations.'}
            {campaign.status === 'inactive' && 'This campaign is currently paused and not accepting donations.'}
            {campaign.status === 'pending_verification' && 'This campaign is pending review and not yet accepting donations.'}
            {!['draft', 'inactive', 'pending_verification'].includes(campaign.status) && 'This campaign is not currently accepting donations.'}
          </p>
          <div className="flex gap-3 justify-center mt-6">
            <Button data-testid="view-campaign-btn" onClick={() => navigate(`/campaign/${id}`)} variant="outline">
              View Campaign
            </Button>
            <Button data-testid="browse-active-campaigns-btn" onClick={() => navigate('/discover')}>
              Browse Campaigns
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate(`/campaign/${id}`)}
            className="p-2 hover:bg-secondary rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-foreground truncate">{campaign.title}</h1>
            <p className="text-sm text-muted-foreground">
              {selectedCurrency.symbol}{campaign.raised?.toLocaleString()} {t('raisedLabel')} {t('of')} {selectedCurrency.symbol}{campaign.goal?.toLocaleString()}
            </p>
          </div>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-foreground font-bold text-sm">
            {Math.round(getProgressPercent())}%
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-secondary">
          <div 
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
            style={{ width: `${getProgressPercent()}%` }}
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Team Page Attribution Banner with Progress */}
        {teamPage && (
          <div className="bg-gradient-to-r from-violet-500/10 to-indigo-500/10 border border-violet-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Donating via</p>
                <p className="font-semibold text-foreground">{teamPage.title}</p>
                <p className="text-xs text-muted-foreground">by {teamPage.owner_name}</p>
              </div>
              {teamPage.goal_amount > 0 && (
                <div className="text-right">
                  <p className="text-sm font-semibold text-violet-400">
                    ${(teamPage.amount_raised || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    of ${teamPage.goal_amount.toLocaleString()} goal
                  </p>
                  <p className="text-xs text-violet-400">
                    {teamPage.goal_amount > 0 ? Math.round((teamPage.amount_raised / teamPage.goal_amount) * 100) : 0}%
                  </p>
                </div>
              )}
            </div>
            {/* Progress bar for team page */}
            {teamPage.goal_amount > 0 && (
              <div className="mt-3 h-1.5 bg-violet-500/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-violet-500 transition-all duration-500"
                  style={{ width: `${Math.min((teamPage.amount_raised / teamPage.goal_amount) * 100, 100)}%` }}
                />
              </div>
            )}
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Amount Selection */}
          <div className="space-y-6">
            {/* Currency Selector */}
            <div className="relative">
              <label className="block text-sm font-medium text-foreground mb-2">{t('currencyLabel')}</label>
              <button
                type="button"
                onClick={() => {
                  setShowCurrencyDropdown(!showCurrencyDropdown);
                  setCurrencySearch('');
                }}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-secondary border border-border rounded-xl hover:bg-secondary/80 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{selectedCurrency.flag}</span>
                  <span className="font-medium">{selectedCurrency.code}</span>
                  <span className="text-muted-foreground">({selectedCurrency.symbol})</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showCurrencyDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-lg z-50">
                  <div className="p-2 border-b border-border">
                    <Input
                      type="text"
                      placeholder="Search currency..."
                      value={currencySearch}
                      onChange={(e) => setCurrencySearch(e.target.value)}
                      className="h-10"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {filteredCurrencies.slice(0, 30).map((currency) => (
                      <button
                        key={currency.code}
                        type="button"
                        onClick={() => {
                          setSelectedCurrency(currency);
                          setShowCurrencyDropdown(false);
                          setCurrencySearch('');
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary transition-colors text-left ${
                          selectedCurrency.code === currency.code ? 'bg-emerald-500/20' : ''
                        }`}
                      >
                        <span className="text-xl">{currency.flag}</span>
                        <span className="font-medium">{currency.code}</span>
                        <span className="text-sm text-muted-foreground">{currency.name}</span>
                        <span className="ml-auto text-muted-foreground">{currency.symbol}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Amount Display */}
            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-6">
              <label className="block text-sm font-medium text-foreground mb-3">{t('enterDonation')}</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-3xl font-light text-muted-foreground">
                  {selectedCurrency.symbol}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={customAmount || donationAmount}
                  onChange={(e) => {
                    // Only allow numbers and decimal point
                    const val = e.target.value.replace(/[^0-9.]/g, '');
                    setCustomAmount(val);
                    setDonationAmount('');
                  }}
                  placeholder="0.00"
                  className="w-full text-right text-5xl md:text-6xl font-bold bg-transparent border-none focus:outline-none text-foreground pr-4 py-4 [appearance:textfield]"
                />
              </div>
            </div>

            {/* Preset Amounts */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {presetAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => {
                    setDonationAmount(amount.toString());
                    setCustomAmount('');
                  }}
                  className={`py-3 px-2 rounded-xl font-medium text-sm transition-all ${
                    donationAmount === amount.toString()
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg scale-105'
                      : 'bg-secondary hover:bg-secondary/80 text-foreground border border-border'
                  }`}
                >
                  {selectedCurrency.symbol}{amount}
                </button>
              ))}
            </div>
            
            {/* Show remaining amount warning if campaign is close to goal */}
            {campaign && campaign.goal > 0 && (() => {
              const remaining = campaign.goal - (campaign.raised || 0);
              if (remaining <= 0) {
                return (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center">
                    <p className="text-amber-500 text-sm font-medium">
                      This campaign has reached its goal! Thank you for your interest.
                    </p>
                  </div>
                );
              } else if (remaining < 1000) {
                return (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-center">
                    <p className="text-emerald-400 text-sm">
                      Only <span className="font-bold">{selectedCurrency.symbol}{remaining.toFixed(2)}</span> remaining to reach the goal!
                    </p>
                  </div>
                );
              }
              return null;
            })()}

            {/* One-time vs Monthly Toggle */}
            <div className="bg-card border border-border rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-5 h-5 text-violet-500" />
                    <span className="font-medium text-foreground">Make it monthly</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {isRecurring 
                      ? `You'll be charged ${selectedCurrency.symbol}${getBaseAmount().toFixed(2)}/month until you cancel`
                      : 'Provide ongoing support with a recurring donation'
                    }
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsRecurring(!isRecurring)}
                  className={`relative w-14 h-7 rounded-full transition-all duration-200 ${
                    isRecurring 
                      ? 'bg-gradient-to-r from-violet-500 to-indigo-600' 
                      : 'bg-secondary'
                  }`}
                >
                  <span 
                    className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-all duration-200 ${
                      isRecurring ? 'left-7' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
              
              {isRecurring && (
                <div className="mt-4 p-3 bg-violet-500/10 border border-violet-500/20 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">Monthly subscription benefits:</p>
                      <ul className="space-y-1">
                        <li>• Cancel anytime from your dashboard</li>
                        <li>• Help creators plan with predictable income</li>
                        <li>• Get exclusive supporter updates</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tip Section - Hide for recurring */}
            {!isRecurring && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium">{t('tipFundTheHope')}</span>
                </div>
                <span className="text-sm text-muted-foreground">{t('helpKeepPlatformFree')}</span>
              </div>
              
              {/* Tip Slider */}
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('yourTip')}</span>
                  <span className="font-medium text-amber-500">
                    {showCustomTip 
                      ? `${selectedCurrency.symbol}${customTipAmount || '0'}`
                      : `${tipPercentage}%`
                    }
                  </span>
                </div>
                
                {!showCustomTip ? (
                  <>
                    {/* Custom styled slider */}
                    <div className="relative pt-1">
                      <div className="relative h-3 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="absolute left-0 top-0 h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-200"
                          style={{ width: `${(tipPercentage / 30) * 100}%` }}
                        />
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="30"
                        value={tipPercentage}
                        onChange={(e) => setTipPercentage(parseInt(e.target.value))}
                        className="absolute top-0 left-0 w-full h-3 opacity-0 cursor-pointer"
                      />
                      {/* Thumb indicator */}
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-amber-500 rounded-full shadow-md transition-all duration-200 pointer-events-none"
                        style={{ left: `calc(${(tipPercentage / 30) * 100}% - 10px)` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>0%</span>
                      <span>10%</span>
                      <span>20%</span>
                      <span>30%</span>
                    </div>
                  </>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {selectedCurrency.symbol}
                    </span>
                    <Input
                      type="number"
                      placeholder="Enter custom tip"
                      value={customTipAmount}
                      onChange={(e) => setCustomTipAmount(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                )}
                
                <button
                  onClick={() => {
                    setShowCustomTip(!showCustomTip);
                    if (!showCustomTip) setTipPercentage(0);
                  }}
                  className="text-sm text-emerald-500 hover:text-emerald-400 transition-colors"
                >
                  {showCustomTip ? '← Use percentage' : 'Enter custom tip →'}
                </button>
              </div>
            </div>
            )}
          </div>

          {/* Right Column - Payment & Info */}
          <div className="space-y-6">
            {/* Payment Method */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-medium mb-4">{t('paymentMethod')}</h3>
              <div className="space-y-2">
                {/* Credit/Debit Card - Primary option */}
                <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                  paymentMethod === 'card' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:bg-secondary'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'card' ? 'border-emerald-500' : 'border-muted-foreground'
                  }`}>
                    {paymentMethod === 'card' && <div className="w-3 h-3 rounded-full bg-emerald-500" />}
                  </div>
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                  <span className="flex-1">{t('creditOrDebitCard')}</span>
                  <span className="text-xs text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">{t('recommended')}</span>
                </label>

                {/* Apple Pay */}
                <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                  paymentMethod === 'apple_pay' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:bg-secondary'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="apple_pay"
                    checked={paymentMethod === 'apple_pay'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'apple_pay' ? 'border-emerald-500' : 'border-muted-foreground'
                  }`}>
                    {paymentMethod === 'apple_pay' && <div className="w-3 h-3 rounded-full bg-emerald-500" />}
                  </div>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <span className="flex-1">Apple Pay</span>
                  <span className="text-xs text-emerald-500">via Stripe</span>
                </label>

                {/* Google Pay */}
                <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                  paymentMethod === 'google_pay' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:bg-secondary'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="google_pay"
                    checked={paymentMethod === 'google_pay'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'google_pay' ? 'border-emerald-500' : 'border-muted-foreground'
                  }`}>
                    {paymentMethod === 'google_pay' && <div className="w-3 h-3 rounded-full bg-emerald-500" />}
                  </div>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="flex-1">Google Pay</span>
                  <span className="text-xs text-emerald-500">via Stripe</span>
                </label>

                {/* QR Code */}
                <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all opacity-60 ${
                  paymentMethod === 'qr_code' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:bg-secondary'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="qr_code"
                    checked={paymentMethod === 'qr_code'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'qr_code' ? 'border-emerald-500' : 'border-muted-foreground'
                  }`}>
                    {paymentMethod === 'qr_code' && <div className="w-3 h-3 rounded-full bg-emerald-500" />}
                  </div>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM13 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM17 13h2v2h-2zM19 15h2v2h-2zM17 17h2v2h-2zM15 19h2v2h-2zM19 19h2v2h-2z"/>
                  </svg>
                  <span className="flex-1">QR Code Payment</span>
                  <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full">Coming Soon</span>
                </label>

              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Apple Pay & Google Pay are available in Stripe checkout if your device supports them.
              </p>
            </div>

            {/* Donor Info */}
            <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
              <h3 className="font-medium">{t('yourInformation')}</h3>
              
              <div>
                <label className="block text-sm text-muted-foreground mb-1">
                  {t('email')} {isRecurring ? <span className="text-red-500">*</span> : <span className="text-xs">({t('optionalForReceipt')})</span>}
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  required={isRecurring}
                  className={isRecurring && !donorEmail ? 'border-amber-500' : ''}
                />
                {isRecurring && !donorEmail && (
                  <p className="text-xs text-amber-500 mt-1">Email is required for monthly subscriptions</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">{t('firstName')}</label>
                  <Input
                    type="text"
                    placeholder={t('firstName')}
                    value={donorFirstName}
                    onChange={(e) => setDonorFirstName(e.target.value)}
                    disabled={hideFromPublic}
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">{t('lastName')}</label>
                  <Input
                    type="text"
                    placeholder={t('lastName')}
                    value={donorLastName}
                    onChange={(e) => setDonorLastName(e.target.value)}
                    disabled={hideFromPublic}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-muted-foreground mb-1">{t('leaveMessageOptional')}</label>
                <Textarea
                  placeholder={t('writeMessage')}
                  value={donorMessage}
                  onChange={(e) => setDonorMessage(e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            {/* Privacy Options */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  hideFromPublic ? 'bg-emerald-500 border-emerald-500' : 'border-muted-foreground group-hover:border-emerald-500'
                }`}>
                  {hideFromPublic && <Check className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={hideFromPublic}
                  onChange={(e) => setHideFromPublic(e.target.checked)}
                  className="sr-only"
                />
                <div>
                  <span className="text-sm font-medium">{t('dontDisplayPublicly')}</span>
                  <p className="text-xs text-muted-foreground">{t('donationShowAsAnonymous')}</p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  optInMarketing ? 'bg-emerald-500 border-emerald-500' : 'border-muted-foreground group-hover:border-emerald-500'
                }`}>
                  {optInMarketing && <Check className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={optInMarketing}
                  onChange={(e) => setOptInMarketing(e.target.checked)}
                  className="sr-only"
                />
                <div>
                  <span className="text-sm font-medium">{t('getUpdatesFromFundTheHope')}</span>
                  <p className="text-xs text-muted-foreground">{t('unsubscribeAnytime')}</p>
                </div>
              </label>
            </div>

            {/* Donation Summary */}
            <div className="bg-secondary/50 border border-border rounded-2xl p-5 space-y-3">
              <h3 className="font-medium">{isRecurring ? 'Monthly Subscription' : t('yourDonation')}</h3>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{isRecurring ? 'Monthly amount' : t('yourDonation')}</span>
                <span className="font-medium">
                  {selectedCurrency.symbol}{getBaseAmount().toFixed(2)}
                  {isRecurring && '/month'}
                </span>
              </div>
              {!isRecurring && getTipAmount() > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('fundTheHopeTip')}</span>
                  <span className="font-medium text-amber-500">{selectedCurrency.symbol}{getTipAmount().toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-border pt-3 flex justify-between">
                <span className="font-semibold">{isRecurring ? 'Due today (then monthly)' : t('totalDueToday')}</span>
                <span className={`font-bold text-xl ${isRecurring ? 'text-violet-400' : 'text-emerald-400'}`}>
                  {selectedCurrency.symbol}{isRecurring ? getBaseAmount().toFixed(2) : getTotalAmount().toFixed(2)}
                </span>
              </div>
              {isRecurring && (
                <p className="text-xs text-violet-400">
                  You can cancel anytime from your dashboard
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('internationalFeesMayApply')}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-500 text-sm">
                {error}
              </div>
            )}

            {/* Donate Button */}
            <Button
              onClick={handleDonate}
              disabled={isSubmitting || getBaseAmount() <= 0 || (isRecurring && !donorEmail)}
              className={`w-full py-6 text-lg font-semibold rounded-xl ${
                isRecurring 
                  ? 'bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700' 
                  : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('processing')}
                </>
              ) : isRecurring ? (
                <>
                  <Sparkles className="w-5 h-5 mr-2" /> 
                  Start Monthly Donation {getBaseAmount() > 0 ? `${selectedCurrency.symbol}${getBaseAmount().toFixed(2)}/mo` : ''}
                </>
              ) : (
                <>
                  <Heart className="w-5 h-5 mr-2" /> 
                  {t('donateNow')} {getTotalAmount() > 0 ? `${selectedCurrency.symbol}${getTotalAmount().toFixed(2)}` : ''}
                </>
              )}
            </Button>

            {/* Trust Badge */}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span>{t('fundTheHopeProtects')}</span>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              {t('byDonatingYouAgree')}{' '}
              <Link to="/terms" className="text-emerald-500 hover:underline">{t('termsOfService')}</Link>{' '}
              {t('and')}{' '}
              <Link to="/privacy" className="text-emerald-500 hover:underline">{t('privacyPolicy')}</Link>.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DonatePage;
